import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Activity, Clock, AlertCircle, CheckCircle, Search, Key } from "lucide-react";
import { apiLogService, apiKeyService, profileService } from "@/services";

interface ApiLogsTabProps {
  initialApiKeyId?: string | null;
}

type DateFilter = "7days" | "30days" | "90days" | "all";

interface ApiKey {
  id: string;
  name: string;
}

export function ApiLogsTab({ initialApiKeyId }: ApiLogsTabProps) {
  const { user } = useAuth();
  const [apiKeyFilter, setApiKeyFilter] = useState<string>(initialApiKeyId || "all");
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [endpointFilter, setEndpointFilter] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("7days");

  // Fetch user's profile to get company_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  // Fetch all API keys for the dropdown
  const { data: apiKeys } = useQuery({
    queryKey: ["api-keys-list", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const result = await apiKeyService.list(companyId);
      if (result.error) throw result.error;
      return [...result.data]
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((k) => ({ id: k.id, name: k.name })) as ApiKey[];
    },
    enabled: !!companyId,
  });

  // Calculate date range based on filter
  const dateRangeStart = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case "7days":
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case "30days":
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case "90days":
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      default:
        return undefined;
    }
  }, [dateFilter]);

  // Fetch API logs
  const { data: logs, isLoading } = useQuery({
    queryKey: ["api-logs-company", companyId, apiKeyFilter, methodFilter, endpointFilter, dateFilter],
    queryFn: async () => {
      if (!companyId) return [];
      const result = await apiLogService.listByCompany(companyId, {
        apiKeyId: apiKeyFilter !== "all" ? apiKeyFilter : undefined,
        method: methodFilter,
        endpoint: endpointFilter || undefined,
        dateRangeStart,
        limit: 500,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!companyId,
  });

  // Calculate stats
  const stats = useMemo(() => {
    if (!logs) {
      return { total: 0, success: 0, errors: 0, avgResponseTime: 0 };
    }

    const success = logs.filter((l) => l.status_code && l.status_code >= 200 && l.status_code < 300).length;
    const errors = logs.filter((l) => l.status_code && l.status_code >= 400).length;
    const avgResponseTime = logs.length
      ? Math.round(logs.reduce((acc, l) => acc + (l.response_time_ms || 0), 0) / logs.length)
      : 0;

    return { total: logs.length, success, errors, avgResponseTime };
  }, [logs]);

  const getStatusBadge = (statusCode: number | null) => {
    if (!statusCode) return <Badge variant="outline">—</Badge>;
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <Badge variant="default" className="bg-green-500">
          {statusCode}
        </Badge>
      );
    }
    if (statusCode >= 400 && statusCode < 500) {
      return (
        <Badge variant="secondary" className="bg-amber-500 text-white">
          {statusCode}
        </Badge>
      );
    }
    if (statusCode >= 500) {
      return <Badge variant="destructive">{statusCode}</Badge>;
    }
    return <Badge variant="outline">{statusCode}</Badge>;
  };

  const getMethodBadge = (method: string) => {
    const colors: Record<string, string> = {
      GET: "bg-blue-500",
      POST: "bg-green-500",
      PUT: "bg-amber-500",
      PATCH: "bg-orange-500",
      DELETE: "bg-red-500",
    };
    return <Badge className={colors[method] || "bg-gray-500"}>{method}</Badge>;
  };

  const hasActiveFilters =
    apiKeyFilter !== "all" || methodFilter !== "all" || endpointFilter || dateFilter !== "7days";

  const clearFilters = () => {
    setApiKeyFilter("all");
    setMethodFilter("all");
    setEndpointFilter("");
    setDateFilter("7days");
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.total}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.success}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Errors</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.errors}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : `${stats.avgResponseTime}ms`}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={apiKeyFilter} onValueChange={setApiKeyFilter}>
          <SelectTrigger className="w-[200px]">
            <Key className="h-4 w-4 mr-2" />
            <SelectValue placeholder="API Key" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All API Keys</SelectItem>
            {apiKeys?.map((key) => (
              <SelectItem key={key.id} value={key.id}>
                {key.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="GET">GET</SelectItem>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
            <SelectItem value="DELETE">DELETE</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by endpoint..."
            value={endpointFilter}
            onChange={(e) => setEndpointFilter(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7days">Last 7 days</SelectItem>
            <SelectItem value="30days">Last 30 days</SelectItem>
            <SelectItem value="90days">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>API Key</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Response Time</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  </TableRow>
                ))
              ) : logs && logs.length > 0 ? (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground text-sm">
                      {log.created ? format(new Date(log.created), "MMM d, yyyy h:mm a") : "—"}
                    </TableCell>
                    <TableCell className="font-medium">{log.api_key_name}</TableCell>
                    <TableCell>{getMethodBadge(log.method)}</TableCell>
                    <TableCell>
                      <code className="text-sm">{log.endpoint}</code>
                    </TableCell>
                    <TableCell>{getStatusBadge(log.status_code)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {log.response_time_ms ?? "—"}ms
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm font-mono">
                      {log.ip_address || "—"}
                    </TableCell>
                    <TableCell>
                      {log.error_message && (
                        <span className="text-destructive text-sm">{log.error_message}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4" />
                    <p>No API logs found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {logs && logs.length >= 500 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing the most recent 500 records. Use filters to narrow down results.
        </p>
      )}
    </div>
  );
}
